import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  TrendingUp, 
  Star, 
  Clock, 
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Activity
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CustomerMetrics {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  userRetentionRate: number;
  avgTimeToFirstAction: number;
  completedOnboarding: number;
  supportTicketResolutionTime: number;
  customerSatisfactionScore: number;
  featureAdoptionRates: Array<{
    feature: string;
    adoptionRate: number;
    userCount: number;
  }>;
  userJourney: {
    signups: number;
    profileComplete: number;
    firstListing: number;
    firstReferral: number;
    activeUsers: number;
  };
}

export default function CustomerSuccessMetrics() {
  const [metrics, setMetrics] = useState<CustomerMetrics>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    userRetentionRate: 0,
    avgTimeToFirstAction: 0,
    completedOnboarding: 0,
    supportTicketResolutionTime: 0,
    customerSatisfactionScore: 0,
    featureAdoptionRates: [],
    userJourney: {
      signups: 0,
      profileComplete: 0,
      firstListing: 0,
      firstReferral: 0,
      activeUsers: 0
    }
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const loadCustomerMetrics = async () => {
    try {
      setLoading(true);

      // Get all profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*');

      if (profilesError) throw profilesError;

      // Get listings data
      const { data: listings, error: listingsError } = await supabase
        .from('bed_listings')
        .select('created_by, created_at');

      if (listingsError) throw listingsError;

      // Get referrals data
      const { data: referrals, error: referralsError } = await supabase
        .from('referrals')
        .select('referring_user, created_at');

      if (referralsError) throw referralsError;

      // Get support tickets
      const { data: tickets, error: ticketsError } = await supabase
        .from('support_tickets')
        .select('created_at, resolved_at, status');

      if (ticketsError) throw ticketsError;

      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

      // Calculate basic metrics
      const totalUsers = profiles?.length || 0;
      const newUsersThisMonth = profiles?.filter(p => 
        new Date(p.created_at) >= thisMonth
      ).length || 0;

      // Calculate user journey metrics
      const profilesWithComplete = profiles?.filter(p => 
        p.name && p.email && p.organization
      ).length || 0;

      const usersWithListings = new Set(listings?.map(l => l.created_by)).size;
      const usersWithReferrals = new Set(referrals?.map(r => r.referring_user)).size;

      // Calculate retention (simplified - users active in last 30 days)
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
      const activeUsers = profiles?.filter(p => 
        new Date(p.updated_at) >= thirtyDaysAgo
      ).length || 0;

      const userRetentionRate = totalUsers > 0 ? (activeUsers / totalUsers) * 100 : 0;

      // Calculate support metrics
      const resolvedTickets = tickets?.filter(t => t.status === 'resolved') || [];
      const avgResolutionTime = resolvedTickets.length > 0 
        ? resolvedTickets.reduce((acc, ticket) => {
            if (ticket.resolved_at) {
              const resolution = new Date(ticket.resolved_at).getTime();
              const created = new Date(ticket.created_at).getTime();
              return acc + (resolution - created);
            }
            return acc;
          }, 0) / resolvedTickets.length / (1000 * 60 * 60) // Convert to hours
        : 0;

      // Feature adoption rates
      const featureAdoptionRates = [
        {
          feature: 'Profile Completion',
          adoptionRate: totalUsers > 0 ? (profilesWithComplete / totalUsers) * 100 : 0,
          userCount: profilesWithComplete
        },
        {
          feature: 'Listing Creation',
          adoptionRate: totalUsers > 0 ? (usersWithListings / totalUsers) * 100 : 0,
          userCount: usersWithListings
        },
        {
          feature: 'Referral Submission',
          adoptionRate: totalUsers > 0 ? (usersWithReferrals / totalUsers) * 100 : 0,
          userCount: usersWithReferrals
        }
      ];

      setMetrics({
        totalUsers,
        activeUsers,
        newUsersThisMonth,
        userRetentionRate,
        avgTimeToFirstAction: 24, // Mock data - would need more tracking
        completedOnboarding: profilesWithComplete,
        supportTicketResolutionTime: avgResolutionTime,
        customerSatisfactionScore: 4.2, // Mock data - would need surveys
        featureAdoptionRates,
        userJourney: {
          signups: totalUsers,
          profileComplete: profilesWithComplete,
          firstListing: usersWithListings,
          firstReferral: usersWithReferrals,
          activeUsers
        }
      });

    } catch (error) {
      console.error('Error loading customer metrics:', error);
      toast({
        title: "Error Loading Metrics",
        description: "Failed to load customer success data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCustomerMetrics();
  }, []);

  const getProgressColor = (rate: number) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i} className="animate-pulse">
              <CardContent className="pt-6">
                <div className="h-8 bg-muted rounded mb-2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Customer Success Metrics</h2>
        <Button onClick={loadCustomerMetrics} size="sm" variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.totalUsers}</div>
            <p className="text-xs text-muted-foreground">
              +{metrics.newUsersThisMonth} this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">User Retention</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.userRetentionRate.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">
              {metrics.activeUsers} active users
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Satisfaction Score</CardTitle>
            <Star className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.customerSatisfactionScore}/5</div>
            <p className="text-xs text-muted-foreground">
              Based on support feedback
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Resolution Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.supportTicketResolutionTime.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">
              Support ticket resolution
            </p>
          </CardContent>
        </Card>
      </div>

      {/* User Journey Funnel */}
      <Card>
        <CardHeader>
          <CardTitle>User Journey Funnel</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[
              { label: 'Signups', value: metrics.userJourney.signups, icon: Users },
              { label: 'Profile Complete', value: metrics.userJourney.profileComplete, icon: CheckCircle },
              { label: 'First Listing', value: metrics.userJourney.firstListing, icon: Activity },
              { label: 'First Referral', value: metrics.userJourney.firstReferral, icon: TrendingUp },
              { label: 'Active Users', value: metrics.userJourney.activeUsers, icon: Star }
            ].map((step, index) => {
              const percentage = metrics.userJourney.signups > 0 
                ? (step.value / metrics.userJourney.signups) * 100 
                : 0;
              const Icon = step.icon;

              return (
                <div key={step.label} className="flex items-center space-x-4">
                  <div className="flex items-center space-x-2 w-40">
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{step.label}</span>
                  </div>
                  <div className="flex-1">
                    <Progress value={percentage} className="h-2" />
                  </div>
                  <div className="text-right w-20">
                    <div className="text-sm font-bold">{step.value}</div>
                    <div className="text-xs text-muted-foreground">
                      {percentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Feature Adoption */}
      <Card>
        <CardHeader>
          <CardTitle>Feature Adoption Rates</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {metrics.featureAdoptionRates.map((feature) => (
              <div key={feature.feature} className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{feature.feature}</span>
                    <span className="text-sm text-muted-foreground">
                      {feature.userCount} users ({feature.adoptionRate.toFixed(1)}%)
                    </span>
                  </div>
                  <Progress 
                    value={feature.adoptionRate} 
                    className="h-2"
                  />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}